import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Loader2, Search } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { BaaReader } from "@/components/BaaGate";
import { MODULE_NAMES, HIDDEN_MODULES } from "@/lib/practiceSetupConfig";
import {
  ScreenHead, Section, Field, ListCard, Empty, Skeletons, ActionChip,
  ErrLine, GapList, PS_INPUT, useProfile, useClients,
} from "./psPrimitives";
import { useClient } from "@/lib/clientContext";

/* ---------------- Account & profile ---------------- */
export function AccountScreen({ mode, onOpenDq }) {
  const { data: profile } = useProfile();
  const { data: baa } = useQuery({ queryKey: ["baa-status"], queryFn: async () => (await api.get("/api/edi/baa/status")).data });
  const [baaOpen, setBaaOpen] = useState(false);
  if (!profile) return <Skeletons n={4} />;
  const modules = (profile.allowed_modules || []).filter((m) => !HIDDEN_MODULES.has(m));

  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Your Account · step 1 of 4" title="Account & profile"
        lede="The account revQ signed you in with. Sub-logins inherit it." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
        <Field label="Name">{[profile.first_name, profile.last_name].filter(Boolean).join(" ")}</Field>
        <Field label="Email">{profile.email}</Field>
        <Field label="Role">{profile.role}</Field>
        <Field label="Account">{profile.is_primary ? "Primary account" : "Sub-login"}</Field>
        <Field label="Member since">{profile.created_at ? new Date(profile.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</Field>
        <Field label="Last sign-in">{profile.last_login ? new Date(profile.last_login).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</Field>
      </div>

      <Section title="Business Associate Agreement" testid="ps-baa-section">
        <div className="rounded-[14px] border border-[#E9E9EC] bg-white px-4 py-3.5 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[13px] font-semibold text-[var(--ink)]">
              {baa?.accepted ? `Version ${baa.accepted_version} accepted for ${baa.covered_entity_name}` : "Not yet accepted"}
            </div>
            {baa?.accepted && baa.accepted_at ? (
              <div className="text-[11.5px] text-[var(--gray2)] mt-0.5">{new Date(baa.accepted_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>
            ) : null}
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="ps-baa-view" onClick={() => setBaaOpen(true)}
              className="text-[13px] font-semibold text-[#4646b8] hover:underline">View</button>
            <a href="#" data-testid="ps-baa-download"
              onClick={async (e) => { e.preventDefault(); try { const r = await api.get("/api/edi/baa/download", { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `AriaOne-BAA-v${baa?.current_version || "1.0"}.pdf`; a.click(); URL.revokeObjectURL(u); } catch (ex) { toast.error(describeError(ex)); } }}
              className="text-[13px] font-semibold text-[#4646b8] hover:underline">Download PDF</a>
          </div>
        </div>
      </Section>

      <Section title="Modules on this account" testid="ps-modules-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0">
          {modules.map((m) => (
            <div key={m} className="text-[13px] text-[var(--ink)] py-[5px]">{MODULE_NAMES[m] || m}</div>
          ))}
        </div>
      </Section>

      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-account-full">
          <GapList onOpen={onOpenDq} items={[
            { title: "Account type & team", desc: "Solo vs group, invite sub-logins", dq: 37 },
            { title: "Subscription & billing", desc: "Plan, seats, invoices", dq: 45 },
          ]} />
        </Section>
      )}

      {baaOpen ? (
        <div data-testid="ps-baa-modal" style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setBaaOpen(false); }}>
          <div style={{ background: "#F6F6F4", borderRadius: 20, padding: 28, width: "min(680px,96vw)", boxShadow: "0 30px 80px rgba(20,20,18,.35)" }}>
            <BaaReader onClose={() => setBaaOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- Clients ---------------- */
export function ClientsScreen({ mode, onOpenDq }) {
  const qc = useQueryClient();
  const { data: clients = [], isLoading } = useClients();
  const [open, setOpen] = useState(false);
  const [cid, setCid] = useState("");
  const [desc, setDesc] = useState("");

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/api/edi/note-to-claim/clients", payload)).data,
    onSuccess: () => { setOpen(false); setCid(""); setDesc(""); qc.invalidateQueries({ queryKey: ["clients"] }); },
    onError: (e) => toast.error(e?.response?.status === 409 ? `Client ID ${cid} is already taken.` : describeError(e)),
  });
  const cidValid = /^\d{4}$/.test(cid.trim());

  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Your Account · step 2 of 4" title="Clients"
        lede="A client is the entity you bill on behalf of — a 4-digit ID and a name."
        action={<button data-testid="ps-new-client" onClick={() => setOpen((v) => !v)} className="btn-ink inline-flex items-center gap-1.5"><Plus size={14} /> New client</button>} />

      {open && (
        <form onSubmit={(e) => { e.preventDefault(); if (cidValid && desc.trim()) createMut.mutate({ client_id: cid.trim(), name: desc.trim(), description: desc.trim() }); }}
          className="mb-4 flex items-end gap-4 rounded-[14px] border border-[#E9E9EC] bg-white p-4">
          <div className="flex flex-col gap-0.5 w-[120px]">
            <label className="lf-label">Client ID</label>
            <input className={`${PS_INPUT} font-mono`} placeholder="1000" value={cid} maxLength={4} inputMode="numeric" data-testid="ps-client-id-input" onChange={(e) => setCid(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          </div>
          <div className="flex flex-col gap-0.5 flex-1">
            <label className="lf-label">Name</label>
            <input className={PS_INPUT} placeholder="Queens Medical Assoc." value={desc} data-testid="ps-client-name-input" onChange={(e) => setDesc(e.target.value)} />
          </div>
          <button type="submit" disabled={!cidValid || !desc.trim() || createMut.isPending} data-testid="ps-client-add" className="btn-ink inline-flex items-center gap-1.5">
            {createMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </form>
      )}

      {isLoading ? <Skeletons /> : clients.length === 0 ? <Empty><strong className="text-[var(--ink2)]">No clients yet.</strong> Add your first one.</Empty> : (
        <ListCard testid="ps-clients-list">
          {clients.map((c, i) => (
            <div key={c.id} data-testid={`ps-client-row-${c.client_id}`} className={`flex items-center gap-3 px-4 py-3.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
              <div className="w-[38px] h-[38px] rounded-full bg-[#F1F1F4] text-[var(--gray2)] font-mono font-bold text-[12px] flex items-center justify-center flex-shrink-0">{c.client_id}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{c.description || c.name || "Untitled"}</div>
                <div className="text-[11.5px] text-[var(--gray2)] mt-0.5">
                  {c.linked_npi_count || 0} provider{(c.linked_npi_count || 0) === 1 ? "" : "s"} · {c.roster_count || 0} patients · superbill template: {c.template_name || "none"}
                </div>
                <ErrLine>{c.roster_error}</ErrLine>
              </div>
            </div>
          ))}
        </ListCard>
      )}

      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-clients-full">
          <GapList onOpen={onOpenDq} items={[{ title: "Migrated data totals", desc: "CPT / ICD / locations / modifiers counts per client", dq: 43 }]} />
        </Section>
      )}
    </div>
  );
}

/* ---------------- EHR / PM connection (read-only) ---------------- */
export function EhrScreen({ mode, onOpenDq }) {
  const { data: allClients = [], isLoading } = useClients();
  const { selectedClientId } = useClient();
  const clients = allClients.filter((c) => !selectedClientId || String(c.client_id) === String(selectedClientId));
  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Your Account · step 4 of 4" title="EHR / PM connection"
        lede="Your patient roster syncs from the connected practice-management system." />
      <div className="flex items-center gap-2 mb-4 text-[12px] text-[var(--ink2)]">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--ink)]" />
        Connected with <strong className="font-semibold text-[var(--ink)]">Enable Healthcare</strong>
      </div>
      {isLoading ? <Skeletons /> : (
        <ListCard testid="ps-ehr-list">
          {clients.map((c, i) => {
            const failed = c.roster_status && c.roster_status !== "ready";
            return (
              <div key={c.id} className={`flex items-center gap-3 px-4 py-3.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
                <div className="w-[38px] h-[38px] rounded-full bg-[#F1F1F4] text-[var(--gray2)] font-mono font-bold text-[12px] flex items-center justify-center flex-shrink-0">{c.client_id}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{c.description || c.client_id}</div>
                  <div className="text-[11.5px] text-[var(--gray2)] mt-0.5">
                    {c.roster_count || 0} patients · synced {c.roster_synced_at ? new Date(c.roster_synced_at).toLocaleString() : "—"}
                  </div>
                  <ErrLine>{failed ? c.roster_error || "Import failed" : null}</ErrLine>
                </div>
                {failed ? <ActionChip>Import failed</ActionChip> : (
                  <span className="text-[11px] font-medium text-[var(--gray2)]">Ready</span>
                )}
              </div>
            );
          })}
        </ListCard>
      )}
      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-ehr-full">
          <GapList onOpen={onOpenDq} items={[
            { title: "Connection setup", desc: "Add / re-authorize an EHR or PM system", dq: 24 },
            { title: "What syncs", desc: "Choose which data flows in", dq: 47 },
          ]} />
        </Section>
      )}
    </div>
  );
}

/* ---------------- Payer list (read-only) ---------------- */
export function PayersFullOnly({ onOpenDq }) {
  return (
    <Section title="Full state · needs API" testid="ps-payers-full">
      <GapList onOpen={onOpenDq} items={[{ title: "Payer enrollment", desc: "ERA/EFT enrollment status per payer", dq: 25 }]} />
    </Section>
  );
}

export function PayersScreen({ mode, onOpenDq }) {
  const { data: clients = [] } = useClients();
  const { selectedClientId } = useClient();
  const clientId = selectedClientId || clients[0]?.client_id;
  const [q, setQ] = useState("");
  const { data: payers = [], isLoading } = useQuery({
    queryKey: ["payers", clientId],
    enabled: !!clientId,
    queryFn: async () => (await api.get(`/api/edi/note-to-claim/payers?client_id=${clientId}`)).data?.payers ?? [],
  });
  const filtered = payers.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fade-up">
      <ScreenHead eyebrow="Connections & Enrollment" title="Payer list"
        lede={clientId ? `${payers.length} payers · client ${clientId}` : "Add a client to load payers."} />
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gray3)]" />
        <input className={`${PS_INPUT} pl-9`} placeholder="Search payers…" value={q} data-testid="ps-payer-search" onChange={(e) => setQ(e.target.value)} />
      </div>
      {isLoading ? <Skeletons n={5} /> : filtered.length === 0 ? <Empty>No payers{q ? " match your search." : " for this client."}</Empty> : (
        <ListCard testid="ps-payers-list">
          {filtered.map((p, i) => (
            <div key={p.id || i} className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
              <div className="flex-1 min-w-0"><div className="text-[12.5px] font-medium text-[var(--ink)] truncate">{p.name}</div></div>
              {p.id ? <span className="text-[10px] font-mono text-[var(--gray3)]">{p.id}</span> : null}
            </div>
          ))}
        </ListCard>
      )}
      {mode === "full" && (
        <Section title="Full state · needs API" testid="ps-payers-full">
          <GapList onOpen={onOpenDq} items={[
            { title: "Payer enrollment", desc: "ERA/EFT enrollment status per payer", dq: 25 },
          ]} />
        </Section>
      )}
    </div>
  );
}
